/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import {
  useState,
  useEffect,
  FC,
  Dispatch,
  SetStateAction,
  useCallback,
} from "react";
import {
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Box,
  FormControlLabel,
  Switch,
  SwitchProps,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { PanelContentsContainer, getTaskPlanNodeIds } from "./Util";
import { useTaskDataContext } from "context/TaskDataProvider";
import { useTranslation } from "react-i18next";
import { TaskControlState, PHASE_TASK_SETTING } from "./consts";
import { DatePickerInput } from "components/Common/DatePickerInput";
import DatePicker from "react-datepicker";
import { Const } from "const";
import {
  formatDate,
  isFuture,
  isInRange,
  isToday,
  mergeDateAndTime,
  toMoment,
} from "utils/DateTime";
import { useUserContext } from "context/UserContext";
import { DesktopTimePicker } from "@mui/x-date-pickers/DesktopTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { CountTextField } from "./CountTextField";
import { TaskControlMode } from "./TaskControlDialog";

/** Validation result */
export interface SettingValidation {
  /** Task name input */
  taskName: boolean;
  /** Start date input */
  date: boolean;
  /** Start time input */
  time: boolean;
}

interface SettingPanelProps {
  state: TaskControlState;
  mode: TaskControlMode;
  onValidated?: (validation: SettingValidation) => void;
  setHighlight: Dispatch<SetStateAction<number[]>>;
}

export const SettingPanel: FC<SettingPanelProps> = ({
  state,
  mode,
  onValidated,
  setHighlight,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { taskData, setTaskData } = useTaskDataContext();
  const { jobsite } = useUserContext();

  const [validation, setValidation] = useState({
    taskName: true,
    date: true,
    time: true,
  });

  // task name
  const handleTaskName = (name: string) => {
    const newName = removeMultibyteCharacters(name);
    // Validate
    setValidation((current) => {
      return {
        ...current,
        taskName: validateTaskName(newName),
      };
    });

    setTaskData((pre) => {
      return {
        ...pre,
        jobSetName: newName,
      };
    });
  };

  const removeMultibyteCharacters = (str: string): string => {
    const multibytePattern = /[\u0080-\uFFFF]/g;
    return str.replace(multibytePattern, "");
  };

  // start date/ time
  const [selectedDate, setSelectedDate] = useState(
    taskData.startDatetime ?? new Date()
  );
  const [selectedTime, setSelectedTime] = useState(
    taskData.startDatetime ?? new Date()
  );
  const [alignment, setAlignment] = useState(
    taskData.startDatetime ? "Reserve" : "Now"
  );
  const [isFormEnabled, setIsFormEnabled] = useState(!!taskData.startDatetime);
  const handleChange = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    newAlignment: string
  ) => {
    if (newAlignment) {
      setAlignment(newAlignment);
      setIsFormEnabled(newAlignment === "Reserve");
    }
  };

  // date
  const handleDateChange = (date: Date | null) => {
    if (date) {
      setSelectedDate(date);
    }
  };

  // time
  const handleTimeChange = (time: Date | null) => {
    if (time !== null) {
      setSelectedTime(time);
    }
  };
  useEffect(() => {
    // Validate
    setValidation((current) => {
      return {
        ...current,
        date: validateDate(selectedDate, jobsite?.timezone),
        time: validateTime(selectedTime, selectedDate, jobsite?.timezone),
      };
    });

    setTaskData((pre) => {
      // Update task data
      return {
        ...pre,
        startDatetime:
          alignment === "Now"
            ? new Date()
            : mergeDateAndTime(selectedDate, selectedTime),
      };
    });
  }, [jobsite?.timezone, alignment, selectedDate, selectedTime, setTaskData]);

  // Memo
  const handleMemo = useCallback(
    (text: string) => {
      setTaskData((pre) => {
        if (pre.memo !== text) {
          return { ...pre, memo: text };
        }
        return pre;
      });
    },
    [setTaskData]
  );

  // MoveToStart
  const handleIsMoveToStart = (checked: boolean) => {
    setTaskData((pre) => {
      return {
        ...pre,
        moveToStart: checked,
      };
    });
  };

  useEffect(() => {
    if (state.process[state.phase] === PHASE_TASK_SETTING) {
      setHighlight(getTaskPlanNodeIds(taskData));
    }
  }, [setHighlight, state, taskData]);

  useEffect(() => {
    onValidated?.({
      ...validation,
      date: alignment === "Now" || validation.date,
      time: alignment === "Now" || validation.time,
    });
  }, [alignment, onValidated, validation]);

  const getDateLabel = useCallback(() => {
    return isToday(selectedDate, jobsite?.timezone)
      ? t("AWVUI-MSG59")
      : formatDate(selectedDate, Const.dateFormat, jobsite?.timezone);
  }, [jobsite?.timezone, selectedDate, t]);

  return (
    <PanelContentsContainer>
      <Typography variant="h6">{t("AWVUI-MSG107")}*:</Typography>
      <TextField
        variant="outlined"
        value={taskData.jobSetName}
        onChange={(e) => handleTaskName(e.target.value)}
        inputProps={{
          maxLength: 50,
          pattern: "^[ -~]+$",
        }}
        error={(taskData.jobSetName?.length ?? 0) === 0}
        sx={{ backgroundColor: "white" }}
      />
      <Typography variant="h6" sx={{ paddingTop: "20px" }}>
        {t("AWVUI-MSG108")}:
      </Typography>
      <StartTimeContainer>
        <ToggleButtonGroup
          color="primary"
          value={alignment}
          exclusive
          onChange={handleChange}
          aria-label="Platform"
          sx={{ width: "100%" }}
        >
          <ToggleButton value="Now" sx={{ width: "100%", color: "black" }}>
            {t("AWVUI-MSG57")}
          </ToggleButton>
          <ToggleButton value="Reserve" sx={{ width: "100%", color: "black" }}>
            {t("AWVUI-MSG58")}
          </ToggleButton>
        </ToggleButtonGroup>
        <TimeInputContainer>
          <DatePicker
            selected={selectedDate}
            onChange={handleDateChange}
            startDate={selectedDate}
            value={getDateLabel()}
            disabled={!isFormEnabled}
            customInput={
              <DatePickerInput valid={alignment === "Now" || validation.date} />
            }
            wrapperClassName="custom-datepicker-wrapper"
          />
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DesktopTimePicker
              value={toMoment(selectedTime)}
              ampm={false}
              timeSteps={Const.taskStartTimeInputStep}
              onChange={(v, c) => handleTimeChange(v?.toDate() ?? null)}
              sx={{
                width: "100px",
                minWidth: 120,
                backgroundColor:
                  alignment === "Now" || validation.time
                    ? "white"
                    : theme.palette.error.main,
              }}
              disabled={!isFormEnabled}
            />
          </LocalizationProvider>
        </TimeInputContainer>
      </StartTimeContainer>
      <Typography variant="h6" sx={{ paddingTop: "20px" }}>
        {t("AWVUI-MSG41")}:
      </Typography>
      <CountTextField
        value={taskData.memo}
        maxLength={150}
        onChanged={handleMemo}
      />
      {mode === "register" && (
        <>
          <Typography variant="h6" sx={{ paddingTop: "20px" }}>
            {t("AWVUI-MSG167")}
          </Typography>
          <FormControlLabel
            control={
              <IOSSwitch
                checked={taskData.moveToStart}
                onChange={(event) => {
                  handleIsMoveToStart(event.target.checked);
                }}
                sx={{ m: 1 }}
                defaultChecked
              />
            }
            label=""
          />
        </>
      )}
    </PanelContentsContainer>
  );
};

const StartTimeContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  gap: theme.spacing(1, 0),
}));
const TimeInputContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  width: "100%",
  gap: theme.spacing(1, 1),
}));

/**
 * Validation check for task name
 * @param{string} taskName Task name
 * @return{boolean} true: Valid / false: Invalid
 */
const validateTaskName = (taskName: string) => {
  return taskName.length > 0;
};

/**
 * Validation check for date
 * @param{Date} date Input date
 * @param{string} timezone Timezone
 * @return{boolean} true: Valid / false: Invalid
 */
const validateDate = (date: Date, timezone?: string) => {
  // Today
  const rangeStart = new Date();
  // A week after
  const rangeEnd = new Date(rangeStart);
  rangeEnd.setDate(rangeEnd.getDate() + 7);

  // Check if input date is in a week
  return isInRange(date, rangeStart, rangeEnd, timezone, "day");
};

/**
 * Validation check for time
 * @param{Date} time Input time
 * @param{Date} date Input date
 * @param{string} timezone Timezone
 * @return{boolean} true: Valid / false: Invalid
 */
const validateTime = (time: Date, date: Date, timezone?: string) => {
  const timeToValidate = mergeDateAndTime(date, time);
  console.debug(`timeToValidate: ${timeToValidate}`);

  // Check if input date is future
  return isFuture(timeToValidate, timezone);
};

// Switch
const IOSSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: theme.palette.mode === "dark" ? "#2ECA45" : "#65C466",
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color:
        theme.palette.mode === "light"
          ? theme.palette.grey[100]
          : theme.palette.grey[600],
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: theme.palette.mode === "light" ? 0.7 : 0.3,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 22,
    height: 22,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === "light" ? "#E9E9EA" : "#39393D",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
}));
